import React, { useCallback, useEffect, useState } from "react";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import LockResetOutlined from "@mui/icons-material/LockResetOutlined";
import { useDispatch, useSelector } from "react-redux";
import { showSuccess } from "../../../../helpers/Notifications/Notification";
import {
  activateEmployee,
  deleteEmployee,
  resetEmployeePassword,
} from "../../../../slices/Admin/userManagement.slice";
import {
  ConfirmDialog,
  IconButton,
  Table,
  TablePanel,
} from "../../../../components/ds";

function ActivationSwitch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={checked ? "On" : "Off"}
      className={`ops-switch activate-switch ${checked ? "is-on" : "is-off"}`}
      onClick={() => onChange(!checked)}
    >
      <span className="ops-switch-thumb" aria-hidden="true" />
      <span className="ops-switch-text" aria-hidden="true">
        {checked ? "On" : "Off"}
      </span>
    </button>
  );
}

const EmployeeListTable = ({
  isListLoading,
  populateEmployeeList,
  PAGE_SIZE,
  onPageSizeChange = null,
  orgId,
  paginationCurrentPage,
  searchItem,
  setPaginationCurrentPage,
  handleDrawerOpen,
  tableData,
  setTableData,
}) => {
  const dispatch = useDispatch();
  const { employeeList } = useSelector((state) => state.userManagement);
  const [paginationTotalPages, setPaginationTotalPages] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const onEmployeeActivation = useCallback(
    (value, email) => {
      dispatch(
        activateEmployee({
          email,
          is_active: value,
          orgId,
        })
      );
    },
    [dispatch, orgId]
  );

  useEffect(() => {
    if (employeeList && employeeList.employee_data) {
      const { employee_data, employee_count } = employeeList;
      setPaginationTotalPages(employee_count);
      const formattedData = employee_data?.map((emp, index) => {
        const domainNames = (emp?.domains || []).map((d) => d.name).filter(Boolean);
        const domainsTitle = domainNames.join("\n");
        return {
          key: index + 1,
          slNo: index + 1 + PAGE_SIZE * (paginationCurrentPage - 1),
          name: emp?.name,
          id: emp?.employee_id,
          email: emp?.email,
          department: emp?.department,
          domains: (
            <div className="domains-count">
              {emp?.domains?.length ? (
                <span
                  className="domains-tooltip"
                  title={domainsTitle}
                  tabIndex={0}
                >
                  {emp.domains.length}
                </span>
              ) : (
                0
              )}
            </div>
          ),
          activation: (
            <div
              className={emp?.is_active ? "active-tag tag" : "inactive-tag tag"}
            >
              {emp?.is_active ? "Active" : "Inactive"}
            </div>
          ),
          activateAction: (
            <ActivationSwitch
              checked={!!emp?.is_active}
              label={`Activate ${emp?.name || "employee"}`}
              onChange={(value) => onEmployeeActivation(value, emp?.email)}
            />
          ),
          raw: emp,
        };
      });
      setTableData(formattedData);
    } else setTableData([]);
  }, [
    employeeList,
    employeeList?.employee_data,
    PAGE_SIZE,
    paginationCurrentPage,
    onEmployeeActivation,
    setTableData,
  ]);

  const handleEmployeeDelete = useCallback(
    (id) => {
      setDeleting(true);
      dispatch(
        deleteEmployee({ employee_id: parseInt(id, 10), orgId }, (response) => {
          showSuccess(response);
          populateEmployeeList(searchItem);
          setPendingDelete(null);
          setDeleting(false);
        })
      );
    },
    [dispatch, orgId, populateEmployeeList, searchItem]
  );

  const handleResetPassword = (email) => {
    dispatch(resetEmployeePassword({ email }, (msg) => showSuccess(msg)));
  };

  const totalCount = paginationTotalPages;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE) || 1);
  const from =
    totalCount === 0 ? 0 : PAGE_SIZE * (paginationCurrentPage - 1) + 1;
  const to = Math.min(PAGE_SIZE * paginationCurrentPage, totalCount);

  const handlePaginationonChange = (pageNumber) => {
    setPaginationCurrentPage(pageNumber);
    populateEmployeeList(searchItem, pageNumber);
  };

  const emptyText = searchItem
    ? "No matching records found."
    : "No employee present in the organization.";

  if (isListLoading) {
    return (
      <div className="emp-table-is-loading ops-page">
        Loading employees…
      </div>
    );
  }

  return (
    <div className="list-table ops-page">
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => !deleting && setPendingDelete(null)}
        onConfirm={() => handleEmployeeDelete(pendingDelete?.id)}
        title="Delete employee?"
        confirmLabel="Delete"
        busy={deleting}
      >
        {pendingDelete ? (
          <p style={{ margin: 0 }}>
            Delete <strong>{pendingDelete.name}</strong>? This cannot be undone.
          </p>
        ) : null}
      </ConfirmDialog>

      <TablePanel
        title="Employees"
        total={totalCount}
        from={from}
        to={to}
        page={paginationCurrentPage}
        totalPages={totalPages}
        pageSize={PAGE_SIZE}
        onPageChange={handlePaginationonChange}
        onPageSizeChange={onPageSizeChange}
        empty={emptyText}
      >
        <Table className="ops-ds-table">
          <thead>
            <tr>
              <th>SN</th>
              <th>Employee name</th>
              <th>Employee ID</th>
              <th>Email</th>
              <th>Department</th>
              <th>Domains</th>
              <th>Activation</th>
              <th />
              <th className="ops-actions" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {(tableData || []).map((row) => (
              <tr key={`${row.id}-${row.slNo}`}>
                <td>{row.slNo}</td>
                <td>{row.name}</td>
                <td>{row.id}</td>
                <td>{row.email}</td>
                <td>{row.department}</td>
                <td>{row.domains}</td>
                <td>{row.activation}</td>
                <td>{row.activateAction}</td>
                <td className="ops-actions">
                  <div style={{ display: "inline-flex", gap: 2 }}>
                    <IconButton
                      label="Edit employee"
                      onClick={() => handleDrawerOpen(row.raw)}
                    >
                      <VisibilityOutlined />
                    </IconButton>
                    <IconButton
                      label="Reset password"
                      onClick={() => handleResetPassword(row.email)}
                    >
                      <LockResetOutlined />
                    </IconButton>
                    <IconButton
                      variant="destructive"
                      label="Delete employee"
                      onClick={() =>
                        setPendingDelete({ id: row.id, name: row.name })
                      }
                    >
                      <CloseOutlined />
                    </IconButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TablePanel>
    </div>
  );
};
export default EmployeeListTable;
